import { Component, OnInit } from '@angular/core';
import {
	UntypedFormControl,
	Validators,
	FormsModule,
	ReactiveFormsModule,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { AppDialogComponent } from 'src/app/components/app-dialog/app-dialog.component';
import { AuthService } from 'src/app/services/auth.service';
import { MatCard } from '@angular/material/card';
import { NgClass } from '@angular/common';
import {
	MatFormField,
	MatLabel,
	MatSuffix,
	MatError,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { PasswordValidationComponent } from '../../components/password-validation/password-validation.component';
import { MatDivider } from '@angular/material/list';
import { TOSAndPrivacyPolicyComponent } from '../../terms-of-use-and-privacy-policy/tos-and-privacy-policy.component';

@Component({
	selector: 'app-reset-password',
	templateUrl: './reset-password.component.html',
	styleUrls: [
		'./reset-password.component.scss',
		'../common/modal.scss',
		'../common/main.scss',
	],
	imports: [
		MatCard,
		NgClass,
		MatFormField,
		MatLabel,
		MatInput,
		FormsModule,
		ReactiveFormsModule,
		MatIconButton,
		MatSuffix,
		MatIcon,
		MatError,
		PasswordValidationComponent,
		MatButton,
		MatDivider,
		TOSAndPrivacyPolicyComponent,
	],
})
export class ResetPasswordComponent implements OnInit {
	token = '';
	passwordFormControl = new UntypedFormControl('', [Validators.required]);
	confirmPasswordFormControl = new UntypedFormControl('', [
		Validators.required,
	]);
	showResetEmailComplete = false;
	hide = true;

	constructor(
		private activeRoute: ActivatedRoute,
		private authService: AuthService,
		private router: Router,
		private dialog: MatDialog
	) {}

	private static validatePassword(
		password: string,
		confirmPassword: string
	): PasswordValidationResult {
		if (password !== confirmPassword) {
			return PasswordValidationResult.notMatching;
		}

		const validPassword =
			password.length >= 6 &&
			password.match(/[A-Z]/) &&
			password.match(/[a-z]/) &&
			password.match(/[0-9]/);
		if (!validPassword) {
			return PasswordValidationResult.invalidPassword;
		}

		return PasswordValidationResult.valid;
	}

	ngOnInit(): void {
		const token: string | null =
			this.activeRoute.snapshot.queryParamMap.get('token');
		this.showTokenValidationUi(token).then(() => {});
	}

	private async showTokenValidationUi(token: string | null): Promise<void> {
		if (token === null || token.length === 0) {
			const dialog = this.dialog.open(AppDialogComponent, {
				data: {
					title: 'Invalid password-reset link',
					message: 'Please request a new password-reset email.',
				},
			});
			lastValueFrom(dialog.afterClosed()).then(() =>
				this.router.navigateByUrl('/forgot-password')
			);
		} else {
			this.token = token;
			const { status } =
				await this.authService.checkPasswordResetTokenStatus(token);
			if (status !== 'ok') {
				const dialog = this.dialog.open(AppDialogComponent, {
					data: {
						title: 'Invalid password-reset link',
						message:
							'The link has expired or was not valid. Please request a new password-reset email.',
					},
				});
				lastValueFrom(dialog.afterClosed()).then(() =>
					this.router.navigateByUrl('/forgot-password')
				);
			}
		}
	}

	async submitPasswordReset(): Promise<void> {
		const result = ResetPasswordComponent.validatePassword(
			this.passwordFormControl.value,
			this.confirmPasswordFormControl.value
		);
		switch (result) {
			case PasswordValidationResult.notMatching:
				this.dialog.open(AppDialogComponent, {
					data: {
						title: 'Error',
						message: 'The password and password confirmation did not match.',
					},
				});
				break;
			case PasswordValidationResult.invalidPassword:
				this.dialog.open(AppDialogComponent, {
					data: {
						title: 'Invalid password',
						message:
							'The new password must be at least 6 characters long, and contain at least 1 uppercase letter, 1 lowercase letter, and 1 numeric value.',
					},
				});
				break;
			case PasswordValidationResult.valid:
				const password = this.passwordFormControl.value;
				this.authService
					.resetPassword(password, this.token)
					.then(() => {
						this.showResetEmailComplete = true;
					})
					.catch((err) => {
						const title = err.code?.toString() ?? 'Error';
						const message = err.message ?? 'Internal Server Error';
						this.dialog.open(AppDialogComponent, { data: { title, message } });
					});
				break;
		}
	}

	onSignInClicked(): void {
		this.router.navigateByUrl('/login');
	}
}

enum PasswordValidationResult {
	valid,
	notMatching,
	invalidPassword,
}
